import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_LOGO, fmtDate, daysUntil } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { rundownStatus, RundownBadge } from "@/lib/rundownStatus";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, CalendarDays, MapPin, Clock, Bell, Check, Star } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TASK_STATUSES = ["TODO", "IN PROGRESS", "REVIEW", "DONE"];


const Section = ({ title, children }) => (
  <div className="card-lux p-5"><h3 className="font-heading text-xl text-burgundy mb-3">{title}</h3>{children}</div>
);

export default function CrewPortal() {
  const { user, logout } = useAuth();
  const [d, setD] = useState(null);
  const [now, setNow] = useState(new Date());
  const [activeTab, setActiveTab] = useState("events");
  const [highlight, setHighlight] = useState(null);
  const [rundownEventId, setRundownEventId] = useState(null);
  const [eventFilter, setEventFilter] = useState("all");

  const load = () => api.get("/crew/me").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);

  const updateStatus = async (taskId, status) => {
    setD((p) => ({ ...p, my_tasks: p.my_tasks.map((t) => t.id === taskId ? { ...t, status } : t) }));
    try { await api.post(`/crew/activity/${taskId}/status`, { status }); toast.success("Status diperbarui"); }
    catch { toast.error("Gagal memperbarui status"); load(); }
  };
  const markRead = async () => {
    try { await api.post("/crew/notifications/read"); await load(); } catch { /* noop */ }
  };
  const openNotif = async (n) => {
    const isTask = n.entity_id && (d.my_tasks || []).some((t) => t.id === n.entity_id);
    const targetTab = isTask ? "activity" : "events";
    const targetId = isTask ? `crew-task-${n.entity_id}` : `crew-ev-${n.event_id}`;
    setActiveTab(targetTab);
    setHighlight(targetId);
    setD((p) => ({ ...p, notifications: p.notifications.map((x) => x.id === n.id ? { ...x, read: true } : x), unread: Math.max(0, (p.unread || 0) - (n.read ? 0 : 1)) }));
    try { if (!n.read) await api.post("/crew/notifications/read", { id: n.id }); } catch { /* noop */ }
    setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => setHighlight(null), 2200);
    }, 150);
  };

  if (d === null) return <div className="min-h-screen flex items-center justify-center bg-cream text-[#7A7A7A]">Memuat portal crew...</div>;

  const events = (d && d.events) || [];
  const evName = (id) => { const e = events.find((x) => x.id === id); return e ? `${e.groom} & ${e.bride}` : ""; };
  const notifs = (d && d.notifications) || [];
  const unread = (d && d.unread) || 0;
  const toMin = (t) => { const [h, m] = String(t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isSameDay = (iso) => { if (!iso) return false; const d1 = new Date(iso), d2 = now; return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate(); };
  // group rundown per event, sorted by time; compute the running (NOW) item per event only if it's the wedding day
  const rundownByEvent = events.map((ev) => {
    const items = ((d && d.rundown) || []).filter((r) => r.event_id === ev.id).sort((a, b) => toMin(a.time) - toMin(b.time) || (a.order || 0) - (b.order || 0));
    let nowId = null;
    if (isSameDay(ev.wedding_date)) {
      for (let i = 0; i < items.length; i++) {
        const start = toMin(items[i].time);
        const end = items[i].end_time ? toMin(items[i].end_time) : (i + 1 < items.length ? toMin(items[i + 1].time) : start + 60);
        if (start <= nowMin && nowMin < end) { nowId = items[i].id; break; }
      }
    }
    return { ev, items, nowId };
  }).filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen bg-cream">
      <header className="glass-header sticky top-0 z-20 h-16 flex items-center px-5 lg:px-10">
        <img src={APP_LOGO} alt="Primrose Organizer" className="h-10 w-auto object-contain" data-testid="crew-logo" />
        <div className="ml-auto flex items-center gap-3">
          <span className="text-sm text-[#4A4A4A] hidden sm:block">{user?.name} • Crew</span>
          <button onClick={logout} data-testid="crew-logout" className="p-2 rounded-lg text-[#7A7A7A] hover:text-burgundy hover:bg-beige"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-heading text-3xl text-burgundy">Halo, {user?.name} 👋</h1>
          <p className="text-sm text-[#7A7A7A]">Event dan tugas yang ditugaskan kepada Anda.</p>
        </div>

        <div className="card-lux p-5 mb-6" data-testid="crew-notifications">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-xl text-burgundy flex items-center gap-2">
              <span className="relative"><Bell className="w-5 h-5" />{unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full bg-red-600 text-white flex items-center justify-center" data-testid="crew-notif-badge">{unread}</span>}</span>
              Notifikasi
            </h3>
            {unread > 0 && <button onClick={markRead} data-testid="crew-notif-read" className="text-xs px-3 py-1.5 rounded-full bg-beige text-burgundy hover:bg-gold/20 inline-flex items-center gap-1"><Check className="w-3 h-3" />Tandai sudah dibaca</button>}
          </div>
          {notifs.length === 0 ? <p className="text-sm text-[#7A7A7A]" data-testid="crew-notif-empty">Belum ada notifikasi.</p> : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {notifs.map((n) => (
                <button key={n.id} onClick={() => openNotif(n)} className={cn("w-full text-left flex items-start gap-3 border-b border-gold/10 pb-2 last:border-0 hover:bg-gold/5 rounded px-2 -mx-2 transition-colors", !n.read && "bg-gold/5")} data-testid="crew-notif-item">
                  <span className={cn("mt-1.5 w-2 h-2 rounded-full shrink-0", n.read ? "bg-gold/30" : "bg-burgundy")} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1A1A]">{n.title}</p>
                    <p className="text-xs text-[#4A4A4A]">{n.message}</p>
                    <p className="text-[10px] text-[#7A7A7A] mt-0.5">{fmtDate(n.created_at)} • Ketuk untuk lihat</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {events.length === 0 ? (
          <div className="card-lux p-10 text-center text-[#7A7A7A]" data-testid="crew-empty">Belum ada event yang ditugaskan kepada Anda.</div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-beige mb-4">
              <TabsTrigger value="events" data-testid="crew-tab-events" className="data-[state=active]:bg-burgundy data-[state=active]:text-white">Event Saya</TabsTrigger>
              <TabsTrigger value="activity" data-testid="crew-tab-activity" className="data-[state=active]:bg-burgundy data-[state=active]:text-white">Tugas Saya</TabsTrigger>
              <TabsTrigger value="rundown" data-testid="crew-tab-rundown" className="data-[state=active]:bg-burgundy data-[state=active]:text-white">Rundown</TabsTrigger>
            </TabsList>

            <TabsContent value="events" className="space-y-4">
              <div className="flex items-center gap-1.5 rounded-full bg-beige p-1 w-fit" data-testid="crew-event-filter">
                {[["all", "Semua"], ["today", "Hari ini"], ["upcoming", "Mendatang"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setEventFilter(k)} data-testid={`crew-filter-${k}`}
                    className={cn("px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors", eventFilter === k ? "bg-burgundy text-white" : "text-burgundy hover:bg-gold/20")}>{lbl}</button>
                ))}
              </div>
              {(() => {
                const filtered = events.filter((ev) => {
                  const cd = daysUntil(ev.wedding_date);
                  if (eventFilter === "today") return cd === 0;
                  if (eventFilter === "upcoming") return cd != null && cd >= 0;
                  return true;
                });
                if (filtered.length === 0) return <p className="text-sm text-[#7A7A7A] py-4" data-testid="crew-events-filtered-empty">Tidak ada event pada filter ini.</p>;
                return filtered.map((ev) => {
                const cd = daysUntil(ev.wedding_date);
                const role = (d.assignments.find((a) => a.event_id === ev.id) || {}).role;
                return (
                  <div key={ev.id} id={`crew-ev-${ev.id}`} className={cn("card-lux p-5 transition-all", highlight === `crew-ev-${ev.id}` && "ring-2 ring-gold shadow-lg")} data-testid="crew-event">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading text-xl text-burgundy">{ev.groom} & {ev.bride}</h3>
                        {role && <span className="inline-flex items-center gap-1 mt-1.5 px-3 py-1 rounded-full bg-gold/20 text-gold-dark text-xs font-bold tracking-wide" data-testid="crew-event-role"><Star className="w-3 h-3" />{role}</span>}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#7A7A7A]">
                          <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtDate(ev.wedding_date)}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{ev.venue}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">Countdown</p>
                        <p className="font-heading text-2xl text-burgundy leading-none">{cd < 0 ? 0 : cd}<span className="text-sm"> hari</span></p>
                      </div>
                    </div>
                  </div>
                );
              });
              })()}
            </TabsContent>

            <TabsContent value="activity">
              <Section title="Tugas yang Ditugaskan ke Saya">
                <div className="space-y-2">
                  {(!d.my_tasks || d.my_tasks.length === 0) && <p className="text-sm text-[#7A7A7A]">Belum ada tugas untuk Anda.</p>}
                  {(d.my_tasks || []).map((t) => (
                    <div key={t.id} id={`crew-task-${t.id}`} className={cn("flex items-center gap-3 border-b border-gold/10 pb-2 last:border-0 transition-all", highlight === `crew-task-${t.id}` && "ring-2 ring-gold rounded-lg -mx-2 px-2 bg-gold/5")} data-testid="crew-task">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#1A1A1A]">{t.name}</p>
                        {evName(t.event_id) && <p className="text-[11px] text-gold-dark font-semibold" data-testid="crew-task-event">{evName(t.event_id)}</p>}
                        {(t.pic || t.pic2) && <p className="text-[11px] text-[#7A7A7A]">PIC: {[t.pic, t.pic2].filter(Boolean).join(", ")}</p>}
                        <p className="text-[11px] text-[#7A7A7A]">Deadline {fmtDate(t.deadline)}</p>
                      </div>
                      <Select value={t.status || "TODO"} onValueChange={(v) => updateStatus(t.id, v)}>
                        <SelectTrigger className="h-8 w-40 text-xs bg-white" data-testid="crew-task-status"><SelectValue /></SelectTrigger>
                        <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </Section>
            </TabsContent>

            <TabsContent value="rundown">
              <Section title="Rundown Acara (Read-only)">
                {rundownByEvent.length === 0 ? <p className="text-sm text-[#7A7A7A]">Rundown belum tersedia.</p> : (() => {
                  const sel = rundownByEvent.find((g) => g.ev.id === rundownEventId) || rundownByEvent[0];
                  return (
                    <>
                      {rundownByEvent.length > 1 && (
                        <div className="mb-4">
                          <Select value={sel.ev.id} onValueChange={setRundownEventId}>
                            <SelectTrigger className="w-full sm:w-80 bg-white" data-testid="crew-rundown-event-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {rundownByEvent.map((g) => <SelectItem key={g.ev.id} value={g.ev.id}>{g.ev.groom} & {g.ev.bride} • {fmtDate(g.ev.wedding_date)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="space-y-2" data-testid="crew-rundown-group">
                        {sel.items.map((r) => {
                          const st = rundownStatus(sel.ev, r, now);
                          const active = st === "ON GOING";
                          return (
                            <div key={r.id} className={cn("flex items-start gap-4 border-b border-gold/10 py-2.5 last:border-0 transition-colors", active && "bg-gold/15 border border-gold/40 rounded-lg -mx-2 px-2")} data-testid="crew-rundown">
                              <span className="font-mono text-sm font-semibold text-burgundy whitespace-nowrap flex items-center gap-1"><Clock className="w-3 h-3 shrink-0" />{r.time}{r.end_time ? ` – ${r.end_time}` : ""}</span>
                              <span className="flex-1 text-sm text-[#1A1A1A] break-words">{r.activity}</span>
                              <div className="shrink-0">{st ? <RundownBadge status={st} /> : <StatusBadge status={r.status} />}</div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </Section>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
